/**
 * Custom hook for managing persona deep-dive chat state
 */
import { useState, useRef, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function usePersonaChat() {
  const [activePersonaId, setActivePersonaId] = useState(null);
  const [deepdiveId, setDeepdiveId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [positionSummary, setPositionSummary] = useState(null);
  const [currentBeliefs, setCurrentBeliefs] = useState(null);
  const [beliefHistory, setBeliefHistory] = useState([]);
  const [reversePersuadeResult, setReversePersuadeResult] = useState(null);
  const [proposalResult, setProposalResult] = useState(null);
  const [topicDimensions, setTopicDimensions] = useState([]);
  const [personaInfo, setPersonaInfo] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const readSSEStream = useCallback(async (response, onEvent) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              onEvent(event);
            } catch (e) {
              console.error('Failed to parse SSE event:', e);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }, []);

  const startDeepDive = useCallback(async (sessionId, personaId) => {
    setError(null);
    setActivePersonaId(personaId);
    setChatMessages([]);
    setStreamingContent('');
    setReversePersuadeResult(null);
    setProposalResult(null);
    setBeliefHistory([]);

    try {
      const response = await fetch(
        `${API_BASE}/api/session/${sessionId}/persona-chat/${personaId}/start`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to start deep dive');
      }

      const data = await response.json();
      setDeepdiveId(data.deepdive_id);
      setPositionSummary(data.position_summary);
      setPersonaInfo(data.persona);
      setTopicDimensions(data.topic_dimensions || []);

      if (data.position_summary) {
        setCurrentBeliefs(data.position_summary);
        setBeliefHistory([data.position_summary]);
      }
    } catch (err) {
      setError(err.message);
      console.error('[DeepDive] Start error:', err);
    }
  }, []);

  const sendMessage = useCallback(async (sessionId, personaId, content) => {
    if (!deepdiveId || isStreaming || !content.trim()) return;

    setIsStreaming(true);
    setStreamingContent('');
    setError(null);

    // Add user message immediately
    setChatMessages(prev => [...prev, { role: 'user', content: content.trim() }]);

    try {
      abortRef.current = new AbortController();
      const response = await fetch(
        `${API_BASE}/api/session/${sessionId}/persona-chat/${personaId}/message`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deepdive_id: deepdiveId, content: content.trim() }),
          signal: abortRef.current.signal,
        }
      );

      if (!response.ok) throw new Error('Failed to send message');

      let accumulated = '';
      await readSSEStream(response, (event) => {
        switch (event.type) {
          case 'content_chunk':
            accumulated += event.content;
            setStreamingContent(accumulated);
            break;
          case 'chat_end':
            setChatMessages(prev => [...prev, { role: 'persona', content: event.content }]);
            setStreamingContent('');
            break;
          case 'belief_update':
            setCurrentBeliefs(event.beliefs);
            setBeliefHistory(prev => [...prev, event.beliefs]);
            break;
          case 'error':
            setError(event.message);
            break;
        }
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        console.error('[DeepDive] Message error:', err);
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [deepdiveId, isStreaming, readSSEStream]);

  const reversePersuade = useCallback(async (sessionId, personaId) => {
    if (!deepdiveId || isStreaming) return;

    setIsStreaming(true);
    setStreamingContent('');
    setReversePersuadeResult(null);
    setError(null);

    try {
      abortRef.current = new AbortController();
      const response = await fetch(
        `${API_BASE}/api/session/${sessionId}/persona-chat/${personaId}/reverse-persuade`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deepdive_id: deepdiveId }),
          signal: abortRef.current.signal,
        }
      );

      if (!response.ok) throw new Error('Failed to start reverse persuade');

      let accumulated = '';
      await readSSEStream(response, (event) => {
        switch (event.type) {
          case 'content_chunk':
            accumulated += event.content;
            setStreamingContent(accumulated);
            break;
          case 'reverse_persuade_result':
            setReversePersuadeResult({
              vulnerabilities: event.vulnerabilities || [],
              raw: event.raw,
            });
            setStreamingContent('');
            break;
          case 'error':
            setError(event.message);
            break;
        }
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [deepdiveId, isStreaming, readSSEStream]);

  const testProposal = useCallback(async (sessionId, personaId, proposal) => {
    if (!deepdiveId || isStreaming || !proposal.trim()) return;

    setIsStreaming(true);
    setStreamingContent('');
    setProposalResult(null);
    setError(null);

    try {
      abortRef.current = new AbortController();
      const response = await fetch(
        `${API_BASE}/api/session/${sessionId}/persona-chat/${personaId}/test-proposal`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deepdive_id: deepdiveId, proposal: proposal.trim() }),
          signal: abortRef.current.signal,
        }
      );

      if (!response.ok) throw new Error('Failed to test proposal');

      let accumulated = '';
      await readSSEStream(response, (event) => {
        switch (event.type) {
          case 'content_chunk':
            accumulated += event.content;
            setStreamingContent(accumulated);
            break;
          case 'proposal_result':
            setProposalResult(event.result || {});
            setStreamingContent('');
            break;
          case 'error':
            setError(event.message);
            break;
        }
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [deepdiveId, isStreaming, readSSEStream]);

  const closeDeepDive = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setActivePersonaId(null);
    setDeepdiveId(null);
    setChatMessages([]);
    setStreamingContent('');
    setIsStreaming(false);
    setPositionSummary(null);
    setCurrentBeliefs(null);
    setBeliefHistory([]);
    setReversePersuadeResult(null);
    setProposalResult(null);
    setTopicDimensions([]);
    setPersonaInfo(null);
    setError(null);
  }, []);

  return {
    // State
    activePersonaId,
    deepdiveId,
    chatMessages,
    streamingContent,
    isStreaming,
    positionSummary,
    currentBeliefs,
    beliefHistory,
    reversePersuadeResult,
    proposalResult,
    topicDimensions,
    personaInfo,
    error,

    // Actions
    startDeepDive,
    sendMessage,
    reversePersuade,
    testProposal,
    closeDeepDive,
  };
}
